<template>
  <div v-if="accounts.length > 0">
    <div class="etn-filter-group">
      <div class="etn-filter">
        <div>
          <label class="dropdown-label" for="sub-meter-filter">Sub meters</label>
        </div>
        <div>
          <SelectButton
            id="sub-meter-filter"
            v-model="subMeterFilter"
            :options="subMeterOptions"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
            @change="onFilterChange('subMeter', $event.value)"
          />
        </div>
      </div>

      <div class="etn-filter">
        <div>
          <label class="dropdown-label" for="automation-filter">Automated meters</label>
        </div>
        <div>
          <SelectButton
            id="automation-filter"
            v-model="automationFilter"
            :options="automationOptions"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
            @change="onFilterChange('automation', $event.value)"
          />
        </div>
      </div>

      <div class="etn-filter">
        <div>
          <label class="dropdown-label" for="meter-user-filter">Meter user</label>
        </div>
        <div>
          <SelectButton
            id="meter-user-filter"
            v-model="meterUserFilter"
            :options="meterUserOptions"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
            @change="onFilterChange('meterUser', $event.value)"
          />
        </div>
      </div>
    </div>
    <Select
      inputId="meter-dropdown"
      v-model="selectedAccountId"
      :options="accountOptions"
      optionGroupLabel="group"
      optionGroupChildren="options"
      optionLabel="label"
      optionValue="value"
      placeholder="Select a meter"
      :filter="true"
      :filterFields="['name', 'meterSerialNumber', 'meterPointNumber']"
      filterPlaceholder="Search by name, MSN or MPN"
      fluid
    >
      <template #option="{ option }">
        <div class="account-option-wrapper">
          <div class="account-option">
            <div class="account-option-icon-container">
              <MeterBoltIcon class="account-option-icon" />
            </div>
            <div class="account-option-details">
              <template v-if="option.meterSerialNumber || option.meterPointNumber">
                <span class="account-option-primary">
                  {{ option.meterSerialNumber || option.meterPointNumber }}
                </span>
                <small class="account-option-secondary">{{ option.name }}</small>
              </template>
              <template v-else>
                <span class="account-option-primary">{{ option.name }}</span>
              </template>
              <small
                v-if="option.meterLocation?.trim()"
                class="account-option-secondary account-option-location"
              >
                <LocationDotIcon class="account-location-icon" />
                {{ option.meterLocation.trim() }}
              </small>
            </div>
          </div>
        </div>
      </template>
      <template #value="{ value, placeholder }">
        <span v-if="value && displaySelectedAccount(value)">
          {{ displaySelectedAccount(value) }}
        </span>
        <span v-else>{{ placeholder }}</span>
      </template>
    </Select>
  </div>
  <div v-else-if="accounts.length === 0">
    <div class="alert alert-warning">No accounts found</div>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref } from 'vue';

import { useStore as useWizardStore } from '@/stores/wizard';

import LocationDotIcon from './icons/LocationDotIcon.vue';
import MeterBoltIcon from './icons/MeterBoltIcon.vue';
import Select from 'primevue/select';
import SelectButton from 'primevue/selectbutton';

type AccountOption = {
  value: string;
  label: string;
  name: string;
  meterPointNumber?: string;
  meterSerialNumber?: string;
  meterLocation?: string;
};

const wizardStore = useWizardStore();

const props = defineProps<{
  accounts: ETNAccount[];
}>();

const selectedAccountId = computed({
  get: () => wizardStore.selectedAccount?._id || '',
  set: (id: string) => {
    wizardStore.accountId = id;
  }
});

const types = ref<string[]>(['Electricity', 'Gas', 'Water', 'Heating', 'Cooling']);

const subMeterFilter = ref<'all' | 'primary-only'>('all');
const automationFilter = ref<'manual-only' | 'all'>('manual-only');
const meterUserFilter = ref<'all' | 'tenant' | 'landlord'>('all');

const subMeterOptions = [
  { label: 'Show', value: 'all' },
  { label: 'Hide', value: 'primary-only' }
];

const automationOptions = [
  { label: 'Show', value: 'all' },
  { label: 'Hide', value: 'manual-only' }
];

const meterUserOptions = [
  { label: 'All', value: 'all' },
  { label: 'Tenants', value: 'tenant' },
  { label: 'Landlords', value: 'landlord' }
];

const filteredAccounts = computed((): ETNAccount[] => {
  let accounts = [...props.accounts];

  if (subMeterFilter.value === 'primary-only') {
    accounts = accounts.filter((account) => !account.parentAccountId);
  }

  if (automationFilter.value === 'manual-only') {
    accounts = accounts.filter((account) => !account.automaticMeterRead);
  }

  if (meterUserFilter.value !== 'all') {
    accounts = accounts.filter((account) => account.meterUser === meterUserFilter.value);
  }

  return accounts.sort((a, b) => a.name.localeCompare(b.name));
});

const accountOptions = computed(() => {
  const accounts = filteredAccounts.value;

  return types.value
    .filter((type) => accounts.some((account) => account.type === type.toLowerCase()))
    .reduce<Array<{ group: string; options: AccountOption[] }>>((optGroups, type) => {
      optGroups.push({
        group: type,
        options: accounts
          .filter((account) => account.type === type.toLowerCase())
          .map((account) => ({
            value: account._id,
            label: account.name,
            name: account.name,
            meterPointNumber: account.meterPointNumber,
            meterSerialNumber: account.meterSerialNumber,
            meterLocation: account.meterLocation
          }))
      });

      return optGroups;
    }, []);
});

const displaySelectedAccount = (value: string) => {
  const match = accountOptions.value
    .flatMap((group) => group.options)
    .find((option) => option.value === value);

  if (!match) return '';

  const identifier = match.meterSerialNumber || match.meterPointNumber;
  return identifier ? `${identifier} - ${match.name}` : match.name;
};

const onFilterChange = (
  filterType: 'subMeter' | 'automation' | 'meterUser',
  filterValue: string
) => {
  const currentAccount = wizardStore.selectedAccount;
  const shouldClearSelection =
    (filterType === 'subMeter' &&
      filterValue === 'primary-only' &&
      currentAccount?.parentAccountId) ||
    (filterType === 'automation' &&
      filterValue === 'manual-only' &&
      currentAccount?.automaticMeterRead) ||
    (filterType === 'meterUser' &&
      filterValue !== 'all' &&
      currentAccount?.meterUser !== filterValue);

  if (shouldClearSelection) {
    wizardStore.accountId = '';
  }
};
</script>
